import { useEffect, useState } from 'react';
import IfcViewer from '../components/IfcViewer';

/**
 * Page PUBLIQUE /viewer-partage?token=XXX — pas d'authentification requise.
 *
 * Flux :
 *   1. Lit le token dans l'URL.
 *   2. GET /api/v1/synthesis/shared-ifc?token=XXX → valide le token et retourne
 *      { filename, project_name, ifc_url, expires_at }.
 *   3. Monte <IfcViewer url={ifc_url} canEdit={false} /> en pleine page.
 *
 * L'IfcViewer existant est réutilisé en mode lecture seule :
 *   - `canEdit={false}` désactive toute action d'édition/annotation.
 *   - `documentId` omis → pas de chargement d'annotations, pas d'upload possible.
 */
type Payload = {
  filename: string;
  project_name: string;
  ifc_url: string;
  expires_at: string | null;
};

function getTokenFromUrl(): string | null {
  try {
    const sp = new URLSearchParams(window.location.search);
    return sp.get('token');
  } catch {
    return null;
  }
}

export default function SharedIfcViewer() {
  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'error'; code: number; message: string }
    | { status: 'ready'; data: Payload }
  >({ status: 'loading' });

  const token = getTokenFromUrl();

  // Détection du contexte file:// : un HTML ouvert en local ne peut pas faire de
  // requête vers l'API du portail (CORS + origine 'file:' unique). On avertit
  // l'utilisateur d'une manière lisible au lieu d'un échec silencieux.
  const isFileProtocol =
    typeof window !== 'undefined' && window.location.protocol === 'file:';

  useEffect(() => {
    if (!token) {
      setState({ status: 'error', code: 400, message: 'Aucun token fourni dans l\'URL.' });
      return;
    }
    if (isFileProtocol) {
      setState({
        status: 'error',
        code: 0,
        message:
          "Cette page a été ouverte depuis un fichier local (file://). Pour afficher la maquette, " +
          "ouvrez-la plutôt depuis un lien http:// ou https:// — par exemple en partageant l'URL " +
          "à la place du fichier HTML.",
      });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `/api/v1/synthesis/shared-ifc?token=${encodeURIComponent(token)}`,
          { credentials: 'omit' }
        );
        if (!res.ok) {
          const txt = await res.text().catch(() => '');
          let msg = 'Lien invalide ou expiré.';
          try {
            const j = JSON.parse(txt);
            if (j?.error) msg = j.error;
          } catch { /* fallback sur message générique */ }
          if (!cancelled) setState({ status: 'error', code: res.status, message: msg });
          return;
        }
        const data = (await res.json()) as Payload;
        if (!cancelled) setState({ status: 'ready', data });
      } catch (e: any) {
        if (!cancelled)
          setState({
            status: 'error',
            code: 0,
            message: `Réseau indisponible : ${e?.message ?? e}`,
          });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, isFileProtocol]);

  if (state.status === 'loading') {
    return (
      <ShellLayout>
        <div style={centerBox}>Chargement de la maquette…</div>
      </ShellLayout>
    );
  }

  if (state.status === 'error') {
    const expired = state.code === 410;
    return (
      <ShellLayout>
        <div style={{ ...centerBox, textAlign: 'center', maxWidth: 560, padding: '40px 24px' }}>
          <h1 style={{ fontSize: 22, margin: '0 0 10px 0', color: '#3D5A45' }}>
            {expired ? 'Lien expiré' : 'Lien invalide'}
          </h1>
          <p style={{ fontSize: 14, color: '#6B7C72', lineHeight: 1.6 }}>{state.message}</p>
          <p style={{ fontSize: 12, color: '#A9A193', marginTop: 24 }}>
            Demandez à l'émetteur du lien d'en générer un nouveau depuis le module Synthèse du
            Portail BIM.
          </p>
        </div>
      </ShellLayout>
    );
  }

  const { data } = state;
  const expiresFr = data.expires_at
    ? new Date(data.expires_at).toLocaleDateString('fr-CH', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      })
    : '—';

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        background: '#0E1411',
        color: '#E8E4DB',
      }}
    >
      <header
        style={{
          padding: '10px 18px',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          alignItems: 'baseline',
          gap: 14,
          background: '#121815',
          flexShrink: 0,
        }}
      >
        <strong style={{ fontSize: 14 }}>{data.project_name}</strong>
        <span style={{ fontSize: 12, color: 'rgba(232,228,219,0.65)' }}>
          Maquette : <code style={{ fontSize: 12 }}>{data.filename}</code>
        </span>
        <span style={{ fontSize: 11, color: 'rgba(232,228,219,0.45)', marginLeft: 'auto' }}>
          Lien valable jusqu'au {expiresFr}
        </span>
      </header>

      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {/* On fait monter IfcViewer en plein espace. canEdit=false désactive
            l'édition et les annotations (aucun panneau d'édition ne s'affichera). */}
        <IfcViewer url={data.ifc_url} height="100%" canEdit={false} />
      </div>

      <footer
        style={{
          padding: '6px 18px',
          borderTop: '1px solid rgba(255,255,255,0.08)',
          fontSize: 11,
          color: 'rgba(232,228,219,0.55)',
          background: '#121815',
          flexShrink: 0,
          textAlign: 'center',
        }}
      >
        Partagé via Portail BIM — lien expire le {expiresFr}
      </footer>
    </div>
  );
}

/* Shell neutre (sans la nav principale du portail) pour les écrans d'erreur/chargement. */
function ShellLayout({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#FAFAF5',
        color: '#3D5A45',
        fontFamily: "-apple-system, 'Segoe UI', Roboto, sans-serif",
      }}
    >
      {children}
    </div>
  );
}

const centerBox: React.CSSProperties = {
  padding: 24,
  background: '#fff',
  border: '1px solid #DDD8CE',
  borderRadius: 12,
  boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
  fontSize: 14,
  color: '#3D5A45',
};
